import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Cog, MapPin, RefreshCw, ScrollText, KeyRound, ArrowRightLeft, Ship } from "lucide-react";
import SitesSettings from "@/pages/SitesSettings";
import AdminXero from "@/pages/AdminXero";
import AdminEvents from "@/pages/AdminEvents";
import AdminAudit from "@/pages/AdminAudit";
import DeveloperPortal from "@/pages/DeveloperPortal";
import PosSyncSettings from "@/pages/PosSyncSettings";
import { useAuth } from "@/context/AuthContext";

const TABS_BASE = [
  { value: "sites", label: "Sites & Wi-Fi", icon: MapPin },
  { value: "xero", label: "Xero Sync", icon: RefreshCw },
  { value: "pos-sync", label: "POS Sync", icon: ArrowRightLeft },
  { value: "events", label: "Events & Cruises", icon: Ship },
  { value: "audit", label: "Audit Log", icon: ScrollText },
];

const TAB_DEVELOPER = { value: "developer", label: "Developer", icon: KeyRound };

export default function AdminSettings() {
  const [params, setParams] = useSearchParams();
  const active = params.get("tab") || "sites";
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPERADMIN";
  const TABS = isSuperAdmin ? [...TABS_BASE, TAB_DEVELOPER] : TABS_BASE;

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-[#1A2744] flex items-center gap-2">
          <Cog className="h-7 w-7 text-[#C9A84C]" /> Settings
        </h1>
        <p className="text-sm text-gray-500 mt-1">Manage sites, Wi-Fi gates, Xero integration, POS sync, local events and cruise schedules{isSuperAdmin ? ", developer API access" : ""} and the audit trail in one place.</p>
      </div>

      <Tabs
        value={active}
        onValueChange={(v) => {
          const next = new URLSearchParams(params);
          next.set("tab", v);
          setParams(next, { replace: true });
        }}
        className="w-full"
      >
        <TabsList className="bg-white border border-gray-200 p-1 rounded-lg flex flex-wrap gap-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger
                key={t.value}
                value={t.value}
                data-testid={`settings-tab-${t.value}`}
                className="data-[state=active]:bg-[#1A2744] data-[state=active]:text-white px-4 py-1.5 text-sm font-medium gap-2"
              >
                <Icon className="h-4 w-4" /> {t.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="sites" className="pt-6">
          <SitesSettings />
        </TabsContent>
        <TabsContent value="xero" className="pt-6">
          <AdminXero />
        </TabsContent>
        <TabsContent value="pos-sync" className="pt-6">
          <PosSyncSettings />
        </TabsContent>
        <TabsContent value="events" className="pt-6">
          <AdminEvents />
        </TabsContent>
        <TabsContent value="audit" className="pt-6">
          <AdminAudit />
        </TabsContent>
        {isSuperAdmin && (
          <TabsContent value="developer" className="pt-6">
            <DeveloperPortal />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
